import { makeVar, useReactiveVar } from "@apollo/client";
import React, { useCallback, useState } from "react";

import { reportErrorToNR } from "../util/reportErrorToNR";

export enum ErrorSeverity {
  HIGH = "high",
  MEDIUM = "medium",
  LOW = "low",
}

export type ErrorReporter = (severity: ErrorSeverity, message: string, errorMeta?: Record<string, unknown>) => void;

const errorVar = makeVar<{ message: string; severity: ErrorSeverity } | undefined>(undefined);

export const useErrorHandler = (severity?: ErrorSeverity) => {
  const error = useReactiveVar(errorVar);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Set initial loading to false after a short delay to allow components to stabilize
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 1000); // 1 second delay to allow initial loading to complete

    return () => clearTimeout(timer);
  }, []);

  const simplifyErrorMeta = (errorMeta?: Record<string, unknown>): Record<string, string> | undefined => {
    if (!errorMeta) {
      return undefined;
    }

    return Object.keys(errorMeta).reduce(
      (acc, key) => {
        if (typeof errorMeta[key] === "object") {
          acc[key] = JSON.stringify(errorMeta[key]);
        } else {
          acc[key] = String(errorMeta[key]);
        }
        return acc;
      },
      {} as Record<string, string>,
    );
  };

  const throwError = useCallback(
    async (error: string | Error, errorMeta: Record<string, unknown> = {}): Promise<void> => {
      reportErrorToNR(error, simplifyErrorMeta(errorMeta));
    },
    [],
  );

  const reportError = useCallback(
    async (severity: ErrorSeverity, message: string, errorMeta: Record<string, unknown> = {}): Promise<void> => {
      throwError(message, errorMeta);

      // Only set error state if not in initial loading phase or if it's a critical error
      if (!isInitialLoading || severity === ErrorSeverity.HIGH) {
        errorVar({ message, severity });
      }
    },
    [throwError, isInitialLoading],
  );

  return {
    reportError,
    error: error?.severity === severity ? error?.message : undefined,
    throwError,
  };
};
